/**
 * Prepares a customer's stored email HTML for the workspace thread.
 *
 * Email HTML is attacker-influenced and was written for a mail client, not for a bubble inside our
 * dashboard. Three things go wrong if it is rendered as-is:
 *
 * - The quoted reply chain comes along, so every customer bubble repeats the thread. The helpdesks
 *   we read store no HTML without the quote (Kim Helpdesk's `html_content.reply` is Html2Text
 *   output and Gorgias puts `body_text` there, so neither is HTML), which is why the tail is cut
 *   here from `full`.
 * - Its CSS stays: the email renders in a sandboxed frame (`EmailHtmlContent`) that contains it.
 * - Its images show, as in any helpdesk, but tracking pixels are dropped so the sender never sees the open.
 *
 * The HTML never goes through `marked`: markdown treats indented lines as a code block, which turned
 * most Gmail and Outlook bodies into escaped source.
 */

import DOMPurify from "dompurify";

/** Markers mail clients put on the quoted history. */
const QUOTED_HISTORY_SELECTORS = [".gmail_quote", 'blockquote[type="cite"]'];

/** Outlook's "From: / Sent:" header; it and everything after it is the forwarded or quoted mail. */
const OUTLOOK_REPLY_HEADER_SELECTOR = "#divRplyFwdMsg";

/** Markup that reaches outside the bubble (global CSS, scripts, embeds, page metadata) or posts data. */
// Media tags are dropped too: like a remote <img>, a <video poster>, <audio> or <source> loads from the
// sender's server and tells them the operator opened the email.
const FORBIDDEN_TAGS = [
  "script",
  "iframe",
  "object",
  "embed",
  "form",
  "link",
  "meta",
  "video",
  "audio",
  "source",
  "track",
  "picture",
];

/** `background` loads a remote image like `<img>` does. Inline styles stay: the frame contains them. */
const FORBIDDEN_ATTRIBUTES = ["background"];

/** Open-tracking endpoints of the common senders (Mailchimp, SendGrid, Mandrill, HubSpot, Klaviyo…). */
const TRACKING_URL = /\/(track\/)?open(\.php|\.gif|\.aspx)?(\?|\/|$)|\/wf\/open|\/o\.gif|\/(pixel|beacon)(\.gif|\.png)?(\?|\/|$)|track\.hubspot|trk\.klaviyo/i;

/** A size of 0 or 1px, from the attribute or the inline style. */
function isTinySize(image: Element, side: "width" | "height", style: string): boolean {
  const attribute = Number(image.getAttribute(side));
  if (image.hasAttribute(side) && Number.isFinite(attribute) && attribute <= 1) return true;
  return new RegExp(`(^|[;\\s])${side}\\s*:\\s*[01](px)?\\s*(;|$|!)`, "i").test(style);
}

/** An image that exists only to report an open: invisible, 1x1, or sent to an open-tracking URL. */
function isTrackingPixel(image: Element): boolean {
  const style = image.getAttribute("style") ?? "";
  if (/display\s*:\s*none|visibility\s*:\s*hidden|opacity\s*:\s*0(\.0+)?\s*(;|$|!)/i.test(style)) return true;
  if (isTinySize(image, "width", style) || isTinySize(image, "height", style)) return true;
  return TRACKING_URL.test(image.getAttribute("src") ?? "");
}

/** Whether a parsed body still shows the reader anything. */
function hasVisibleContent(body: HTMLElement): boolean {
  return (
    (body.textContent ?? "").trim() !== "" || body.querySelector("img") !== null
  );
}

/** Removes the Outlook reply header and every sibling after it. */
function removeOutlookReplyTail(body: HTMLElement): void {
  const header = body.querySelector(OUTLOOK_REPLY_HEADER_SELECTOR);
  if (!header) return;
  // Outlook draws a rule just above the header; it belongs to the quote, not to the reply.
  const previous = header.previousElementSibling;
  if (previous?.tagName === "HR") previous.remove();
  while (header.nextSibling) header.nextSibling.remove();
  header.remove();
}

/**
 * The customer's own words: the stored email with the quoted reply chain removed.
 *
 * Falls back to the full email when cutting the quote would leave nothing, e.g. a bare forward whose
 * whole content is the quoted message.
 */
export function stripQuotedTail(html: string): string {
  const body = new DOMParser().parseFromString(html, "text/html").body;
  body
    .querySelectorAll(QUOTED_HISTORY_SELECTORS.join(","))
    .forEach((node) => node.remove());
  removeOutlookReplyTail(body);
  if (!hasVisibleContent(body)) return html;
  // The email's CSS sits in its <head>; carried along so the frame can lay the email out.
  const styles = Array.from(body.ownerDocument.head.querySelectorAll("style"), (style) => style.outerHTML).join("");
  return styles + body.innerHTML;
}

/** Opens every link in a new tab without giving the target page a handle on the dashboard. */
function hardenLinks(root: DocumentFragment): void {
  root.querySelectorAll("a[href]").forEach((link) => {
    link.setAttribute("target", "_blank");
    link.setAttribute("rel", "noopener noreferrer");
  });
}

/** Drops tracking pixels and images with no source; every other image stays and loads in the frame. */
function removeTrackingPixels(root: DocumentFragment): void {
  root.querySelectorAll("img").forEach((image) => {
    if (!image.getAttribute("src") || isTrackingPixel(image)) image.remove();
  });
}

/** Sanitised HTML safe to render in the email frame. */
export function sanitizeEmailHtml(html: string): string {
  const fragment = DOMPurify.sanitize(html, {
    FORBID_TAGS: FORBIDDEN_TAGS,
    FORBID_ATTR: FORBIDDEN_ATTRIBUTES,
    RETURN_DOM_FRAGMENT: true,
  });
  hardenLinks(fragment);
  removeTrackingPixels(fragment);
  const container = document.createElement("div");
  container.appendChild(fragment);
  return container.innerHTML;
}

/** The frame's page: https images only, nothing else remote; the light canvas emails are designed for, in our font. */
export function emailFrameDocument(safeHtml: string, fontFamily: string): string {
  return [
    "<!doctype html><html><head>",
    `<meta http-equiv="Content-Security-Policy" content="default-src 'none'; style-src 'unsafe-inline'; img-src https: data:; font-src data:">`,
    '<base target="_blank">',
    `<style>html{color-scheme:light}body{margin:0;background:#ffffff;color:#2b2e34;font-family:${fontFamily.replace(/[<>]/g, "")},ui-sans-serif,system-ui,-apple-system,"Segoe UI",sans-serif;font-size:13px;line-height:1.5;overflow-wrap:anywhere;overflow-x:auto}img{max-width:100%;height:auto}body *{font-family:inherit!important}</style>`,
    "</head><body>",
    safeHtml,
    "</body></html>",
  ].join("");
}
